import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Trash2, Plus, Shield, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { customerAPI } from '../../services/api';

const PaymentMethodsTab = () => {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      const res = await customerAPI.getPaymentMethods();
      setMethods(res.data?.payment_methods || []);
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (methodId) => {
    if (!window.confirm('Remove this payment method?')) return;
    try {
      await customerAPI.deletePaymentMethod(methodId);
      setMethods(methods.filter(m => m.id !== methodId));
      toast.success('Payment method removed');
    } catch (error) {
      toast.error('Failed to remove payment method');
    }
  };

  const getCardIcon = (brand) => {
    const icons = {
      visa: '💳',
      mastercard: '💳',
      amex: '💳',
      discover: '💳'
    };
    return icons[brand?.toLowerCase()] || '💳';
  };

  const getCardColor = (brand) => {
    const colors = {
      visa: 'bg-[#D4AF37]/100',
      mastercard: 'bg-orange-500',
      amex: 'bg-[#E6C965]',
      discover: 'bg-orange-400'
    };
    return colors[brand?.toLowerCase()] || 'bg-[var(--background-subtle)]0';
  };

  if (loading) {
    return (
      <div className="card p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-[var(--text-muted)]">Loading payment methods...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-[var(--primary)]" />
            Payment Methods
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            Manage your saved payment methods
          </p>
        </div>
      </div>

      {/* Security Note */}
      <div className="bg-[var(--success)]/10 border border-[var(--success)]/30 rounded-xl p-4 mb-6 flex items-start gap-3">
        <Shield className="w-5 h-5 text-[var(--success)] flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-[var(--success)] font-medium text-sm">Your payment info is secure</p>
          <p className="text-green-700 text-xs mt-1">
            All payment information is securely stored by Stripe. We never store your full card details.
          </p>
        </div>
      </div>

      {methods.length === 0 ? (
        <div className="card p-12 text-center">
          <CreditCard className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
          <h3 className="text-lg font-bold mb-2">No Saved Payment Methods</h3>
          <p className="text-[var(--text-secondary)] mb-4">
            Your payment methods will be saved automatically when you complete a booking.
          </p>
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-4 max-w-md mx-auto">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <p className="text-blue-800 text-sm text-left">
                When you pay for your next booking, you can choose to save your card for faster checkout.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {methods.map((method) => (
            <motion.div
              key={method.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="card overflow-hidden"
              data-testid={`payment-method-${method.id}`}
            >
              {/* Card Header with gradient */}
              <div className={`${getCardColor(method.brand)} p-4 text-white`}>
                <div className="flex items-center justify-between">
                  <span className="text-2xl">{getCardIcon(method.brand)}</span>
                  <span className="text-sm font-medium uppercase">{method.brand || 'Card'}</span>
                </div>
                <div className="mt-4 font-mono text-lg tracking-wider">
                  •••• •••• •••• {method.last4 || '****'}
                </div>
              </div>
              
              {/* Card Details */}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[var(--text-muted)]">Expires</p>
                    <p className="font-medium">
                      {method.exp_month?.toString().padStart(2, '0')}/{method.exp_year?.toString().slice(-2) || '••/••'}
                    </p>
                  </div>
                  {method.is_default && (
                    <span className="text-xs bg-[var(--success)]/15 text-green-700 px-2 py-1 rounded-full">
                      Default
                    </span>
                  )}
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDelete(method.id)}
                  className="w-full mt-4 text-[var(--error)] hover:text-red-700 hover:border-red-300"
                  data-testid={`delete-payment-${method.id}`}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove Card
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Payment History Link */}
      <div className="mt-8 pt-6 border-t border-[var(--border-primary)]">
        <h3 className="font-bold mb-4">Payment History</h3>
        <p className="text-[var(--text-muted)] text-sm">
          View your payment history in the "My Bookings" tab. Each booking shows its payment status and receipt.
        </p>
      </div>
    </div>
  );
};

export default PaymentMethodsTab;
